import React, { useRef, useState } from 'react';

import { SignatureCommit } from '../types';
import { SignaturePad } from './SignaturePad';
import styles from './InitialsPad.module.scss';

type InitialsRow = { id: string; text: string };

export type SessionInitials = { mode: 'draw' | 'type'; value: string };

type Props = {
  rows: InitialsRow[];
  disabled?: boolean;
  preview?: boolean;
  sessionInitials: SessionInitials | null;
  externalInitials?: Record<string, string>;
  onInitialsCaptured: (session: SessionInitials) => void;
  onRowApplied?: (rowId: string, value: string) => void;
};

export function InitialsPad({
  rows,
  disabled,
  preview,
  sessionInitials,
  externalInitials,
  onInitialsCaptured,
  onRowApplied,
}: Props) {
  const [rowInitials, setRowInitials] = useState<Record<string, string>>({});
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [sigMode, setSigMode] = useState<'draw' | 'type'>('draw');
  const commitRef = useRef<SignatureCommit | null>(null);

  const openRow = (id: string) => {
    setSigMode(sessionInitials?.mode ?? 'draw');
    commitRef.current = null;
    setOpenRowId(id);
  };

  const handleApply = (value: string) => {
    if (!openRowId) return;
    setRowInitials((prev) => ({ ...prev, [openRowId]: value }));
    onInitialsCaptured({ mode: sigMode, value });
    onRowApplied?.(openRowId, value);
    setOpenRowId(null);
  };

  const isDataUrl = (val: string) => val.startsWith('data:image');

  return (
    <div className={styles.initialsBlock}>
      {rows.map((row) => {
        const applied = rowInitials[row.id] || externalInitials?.[row.id] || '';
        const isLocked = disabled || preview;
        return (
          <div key={row.id} className={styles.initialsRow}>
            <button
              type='button'
              className={`${styles.initialsBox} ${applied ? styles.applied : ''} ${isLocked ? styles.locked : ''}`}
              onClick={() => !isLocked && openRow(row.id)}
              disabled={isLocked}
              title={isLocked ? undefined : applied ? 'Click to change initials' : 'Click to add initials'}
            >
              {applied ? (
                isDataUrl(applied) ? (
                  <img src={applied} alt='initials' className={styles.initialsImg} />
                ) : (
                  <span className={styles.initialsTyped}>{applied}</span>
                )
              ) : (
                <span className={styles.initialsPlaceholder}>
                  Initial
                  <br />
                  Here
                </span>
              )}
            </button>
            <span className={styles.initialsText}>{row.text}</span>
          </div>
        );
      })}

      {openRowId && (
        <div className={styles.initialsFloatOverlay} onMouseDown={() => setOpenRowId(null)}>
          <div className={styles.initialsFloatContent} onMouseDown={(e) => e.stopPropagation()}>
            <SignaturePad
              mode={sigMode}
              onModeChange={(m) => setSigMode(m)}
              onChange={(_hasSig) => {
                /* managed internally by SignaturePad */
              }}
              onCommit={(c) => {
                commitRef.current = c;
              }}
              onApply={handleApply}
              padMode='initials'
              preloadDataUrl={sessionInitials?.mode === 'draw' ? sessionInitials.value : undefined}
              preloadTyped={sessionInitials?.mode === 'type' ? sessionInitials.value : undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
}
